import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ChatbotService } from 'src/app/core/services/chatbot.service';
import { chatbotAnimations } from 'src/assets/animations/chatbot-animations';
import {
  ChatMessage,
  ChatConversation,
  ChatView,
  HelpCollection,
  HelpArticle,
  ChatOption,
  NavigationRoute
} from 'src/app/core/models/chatbot/chat-message.model';

@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
  animations: chatbotAnimations,
  encapsulation: ViewEncapsulation.None
})
export class ChatbotComponent implements OnInit, OnDestroy {
  // Chatbot state
  isOpen = false;
  currentView: ChatView = ChatView.HOME;
  ChatView = ChatView; // Expose enum to template

  // User input
  userInput = '';
  isTyping = false;
  isInitialTyping = false;

  // Conversations
  conversations: ChatConversation[] = [];
  currentConversation: ChatConversation | null = null;
  messages: ChatMessage[] = [];

  // Help & FAQ
  helpCollections: HelpCollection[] = [];
  searchQuery = '';
  searchResults: HelpArticle[] = [];
  selectedArticle: HelpArticle | null = null;
  isSearching = false;

  // Article content loading
  isLoadingArticle = false;
  articleHtmlContent: SafeHtml | null = null;
  safeHtmlUrl: SafeResourceUrl | null = null;  // For iframe-based HTML rendering (avoids CORS)
  safePdfUrl: SafeResourceUrl | null = null;
  articleMarkdownContent: string | null = null;

  // Loading states
  isLoadingConversations = false;
  isLoadingMessages = false;
  isLoadingHelpCollections = false;

  // Usage limit state
  isUsageLimitExceeded = false;
  usageLimitDetail: string | null = null;

  // Pagination for conversations
  conversationsPage = 0; // Start from page 0
  conversationsSize = 20;
  hasMoreConversations = true;

  // Pagination for messages
  messagesPage = 0; // Start from page 0
  messagesSize = 50;
  hasMoreMessages = true;

  // Track if navigating from search button
  isFromSearchButton = false;

  // Reference to search input
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef<HTMLInputElement>;

  // Quick reply options
  quickReplyOptions: ChatOption[] = [
    {
      id: 'billing',
      label: 'chatbot_option_billing',
      icon: 'bi-receipt',
      action: 'send-message',
      value: 'J\'ai une question de facturation'
    },
    {
      id: 'help',
      label: 'chatbot_option_help',
      icon: 'bi-palette',
      action: 'send-message',
      value: 'J\'ai besoin d\'aide avec mes créations'
    },
    {
      id: 'suggestion',
      label: 'chatbot_option_suggestion',
      icon: 'bi-lightbulb',
      action: 'send-message',
      value: 'J\'ai une suggestion de fonctionnalité'
    },
    {
      id: 'account',
      label: 'chatbot_option_account',
      icon: 'bi-tools',
      action: 'send-message',
      value: 'J\'ai un problème avec mon compte'
    }
  ];

  // Animation state
  bounceState = 'idle';

  // Message expansion state
  expandedMessageId: string | null = null;

  // Current navigation routes (from the last bot message)
  currentNavigationRoutes: NavigationRoute[] = [];

  // Chatbot window expansion state
  isExpanded = false;

  private destroy$ = new Subject<void>();
  private typingTimeout: any = null;

  constructor(
    private chatbotService: ChatbotService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.loadConversations();
    this.loadHelpCollections();

    // Subscribe to language changes to reload help collections
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadHelpCollections();
      });
  }

  ngOnDestroy(): void {
    // Clear typing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  // Toggle chatbot open/close
  toggleChatbot(): void {
    this.isOpen = !this.isOpen;
    this.bounceState = this.isOpen ? 'active' : 'idle';

    if (this.isOpen) {
      if (this.currentView === ChatView.HOME) {
        this.addWelcomeMessage();
      } else if (this.currentView === ChatView.CONVERSATIONS_LIST) {
        this.loadConversations();
      }
    }
  }

  // Close chatbot
  closeChatbot(): void {
    this.isOpen = false;
    this.bounceState = 'idle';
    this.isExpanded = false; // Reset expansion when closing
    this.isInitialTyping = false; // Reset initial typing when closing
    this.isUsageLimitExceeded = false; // Reset usage limit alert
    this.usageLimitDetail = null; // Reset usage limit detail

    // Clear typing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    this.isTyping = false;
  }

  // Navigate between views
  navigateToView(view: ChatView): void {
    // Reset expansion when navigating away from CONVERSATION or ARTICLE views
    if (view !== ChatView.CONVERSATION && view !== ChatView.ARTICLE) {
      this.isExpanded = false;
      this.isInitialTyping = false; // Reset initial typing when navigating away from conversation
    }

    this.currentView = view;

    if (view === ChatView.CONVERSATION && !this.currentConversation) {
      this.startNewConversation();
    }

    // Reload conversations when navigating to conversations list
    if (view === ChatView.CONVERSATIONS_LIST) {
      this.loadConversations();
    }

    // Reset search button flag when navigating to HELP from other places
    if (view === ChatView.HELP) {
      this.isFromSearchButton = false;
    }
  }

  // Navigate to HELP view from search button 
  navigateToHelpFromSearch(): void {
    this.isFromSearchButton = true;
    this.navigateToView(ChatView.HELP);

    // Auto-focus search input when navigating from search button
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    }, 100);
  }

  // Go back to previous view
  goBack(): void {
    // Reset expansion when going back
    this.isExpanded = false;
    this.isInitialTyping = false; // Reset initial typing when going back

    if (this.currentView === ChatView.HELP) {
      this.currentView = ChatView.HOME;
      this.isFromSearchButton = false;
    } else if (this.currentView === ChatView.ARTICLE) {
      this.currentView = ChatView.HELP;
      this.selectedArticle = null;
    } else if (this.currentView === ChatView.CONVERSATION) {
      this.currentView = ChatView.CONVERSATIONS_LIST;
    }
  }

  // Toggle chatbot window expansion
  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  // Send message
  sendMessage(messageText?: string): void {
    const text = messageText || this.userInput.trim();

    if (!text) {
      return;
    }

    // Create or get current conversation
    let isNewConversation = false;
    if (!this.currentConversation) {
      this.currentConversation = this.chatbotService.createConversation(text);
      this.chatbotService.setCurrentConversation(this.currentConversation);
      isNewConversation = true;
    }

    const currentConvId = this.currentConversation.id;
    const isTemporaryId = currentConvId.startsWith('temp_');

    // Stop initial typing indicator if active
    if (this.isInitialTyping) {
      this.isInitialTyping = false;
    }

    // Add user message
    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      text: text,
      isUser: true,
      timestamp: new Date(),
      type: 'text'
    };

    this.messages.push(userMessage);
    this.chatbotService.addMessageToConversation(currentConvId, userMessage);

    // Clear current navigation routes when sending a new message (will be updated with bot response)
    this.currentNavigationRoutes = [];

    // Clear input
    this.userInput = '';

    // Switch to conversation view if not already there
    if (this.currentView !== ChatView.CONVERSATION) {
      this.currentView = ChatView.CONVERSATION;
    }

    // Scroll to bottom immediately after user message
    this.cdr.detectChanges();
    this.scrollToBottom();

    // Clear any existing typing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }

    // Show typing indicator after a short delay
    this.typingTimeout = setTimeout(() => {
      this.isTyping = true;
      this.typingTimeout = null;
    }, 800);

    // Send to API - pass conversationId only if it's not temporary
    const apiConversationId = isTemporaryId ? undefined : currentConvId;

    this.chatbotService.sendMessage(text, apiConversationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          // Clear typing timeout if still pending
          if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = null;
          }
          this.isTyping = false;

          // If this was a new conversation, update the temp ID with real ID from API
          if (isNewConversation && response.conversationId && isTemporaryId) {
            this.chatbotService.updateConversationId(currentConvId, response.conversationId);
            // Update local reference
            if (this.currentConversation) {
              this.currentConversation.id = response.conversationId;
            }
          }

          const botMessage: ChatMessage = {
            id: this.generateMessageId(),
            text: response.message,
            isUser: false,
            timestamp: new Date(),
            type: response.type || 'text',
            extraData: response.extraData
          };

          this.messages.push(botMessage);

          // Update current navigation routes from bot response
          if (botMessage.extraData?.navigationRoutes?.length) {
            this.currentNavigationRoutes = botMessage.extraData.navigationRoutes;
          } else {
            this.currentNavigationRoutes = [];
          }

          if (this.currentConversation) {
            this.chatbotService.addMessageToConversation(
              this.currentConversation.id,
              botMessage
            );
          }

          // Scroll to bottom immediately after bot response
          this.cdr.detectChanges();
          this.scrollToBottom();
        },
        error => {
          // Clear typing timeout if still pending
          if (this.typingTimeout) {
            clearTimeout(this.typingTimeout);
            this.typingTimeout = null;
          }
          this.isTyping = false;

          // Parse error body - could be in error.error or directly in error
          const errorBody = error.error || error;

          // Check if usage limit exceeded
          const isUsageLimitError =
            error.status === 429 &&
            (errorBody?.code === 'USAGE_LIMIT_EXCEEDED' ||
              errorBody?.message?.includes('Usage limit exceeded'));

          if (isUsageLimitError) {
            this.isUsageLimitExceeded = true;
            this.usageLimitDetail = errorBody?.detail ||
              errorBody?.message ||
              'Daily usage limit exceeded';

            // Don't add error message to chat, show alert instead
            this.cdr.detectChanges();
          } else {
            // Show generic error message for other errors
            const errorMessage: ChatMessage = {
              id: this.generateMessageId(),
              text: 'Lo siento, ocurrió un error. Por favor intenta de nuevo.',
              isUser: false,
              timestamp: new Date(),
              type: 'text'
            };
            this.messages.push(errorMessage);

            this.cdr.detectChanges();
            this.scrollToBottom();
          }
        }
      );
  }

  // Handle quick reply option click
  handleQuickReply(option: ChatOption): void {
    if (option.action === 'send-message' && option.value) {
      // Start a new conversation before sending the message
      this.startNewConversation();
      this.sendMessage(option.value);
    } else if (option.action === 'navigate' && option.value) {
      this.navigateToView(option.value as ChatView);
    }
  }

  // Start new conversation
  startNewConversation(): void {
    this.currentConversation = null;
    this.messages = [];
    this.chatbotService.setCurrentConversation(null);
    this.currentView = ChatView.CONVERSATION;
    // Reset expansion when starting new conversation
    this.isExpanded = false;
    this.isUsageLimitExceeded = false; // Reset usage limit alert
    this.usageLimitDetail = null; // Reset usage limit detail

    // Show typing indicator before welcome message in new conversation
    this.isInitialTyping = true;

    // After a delay, hide typing indicator and show welcome message
    setTimeout(() => {
      this.isInitialTyping = false;
      this.addWelcomeMessage();
      this.cdr.detectChanges();
      this.scrollToBottom();
    }, 1500);
  }

  // Load conversation from list
  loadConversation(conversation: ChatConversation): void {
    this.currentConversation = conversation;
    this.chatbotService.setCurrentConversation(conversation);
    this.currentView = ChatView.CONVERSATION;
    this.isExpanded = false;
    this.isInitialTyping = false;

    // Reset pagination for messages (start from page 0)
    this.messagesPage = 0;
    this.hasMoreMessages = true;

    // Load messages from API if not already loaded
    if (conversation.messages.length === 0) {
      this.isLoadingMessages = true;

      this.chatbotService.getConversationMessages(conversation.id, this.messagesPage, this.messagesSize)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          response => {
            this.messages = response.messages;
            conversation.messages = response.messages;

            // Only allow more loading if response has data AND is not last page
            this.hasMoreMessages = !response.last && response.messages.length > 0;
            this.isLoadingMessages = false;
            setTimeout(() => this.scrollToBottom(), 100);
          },
          error => {
            console.error('Error loading conversation messages:', error);
            this.messages = [];
            this.hasMoreMessages = false;
            this.isLoadingMessages = false;
          }
        );
    } else {
      // Use cached messages
      this.messages = conversation.messages;
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  // Delete conversation
  deleteConversation(conversationId: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    this.chatbotService.deleteConversation(conversationId);
    this.loadConversations();

    if (this.currentConversation?.id === conversationId) {
      this.currentConversation = null;
      this.messages = [];
      this.chatbotService.setCurrentConversation(null);
      this.currentView = ChatView.CONVERSATIONS_LIST;
    }
  }

  // Search help articles
  searchHelp(): void {
    if (this.searchQuery.trim().length < 2) {
      this.searchResults = [];
      return;
    }

    this.isSearching = true;

    this.chatbotService.searchHelpArticles(this.searchQuery)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        results => {
          this.searchResults = results;
          this.isSearching = false;
        },
        error => {
          console.error('Error searching articles:', error);
          this.isSearching = false;
        }
      );
  }

  // Open help article - loads full article with multimedia content
  openArticle(article: HelpArticle): void {
    this.selectedArticle = article;
    this.currentView = ChatView.ARTICLE;
    this.isExpanded = false;

    // Reset content states
    this.articleHtmlContent = null;
    this.safeHtmlUrl = null;
    this.safePdfUrl = null;
    this.articleMarkdownContent = null;

    // If article already has multimedia, load the content directly
    if (article.multimedia) {
      this.loadArticleContent(article);
    } else {
      // Load full article from API (this also increments view count)
      this.isLoadingArticle = true;
      this.chatbotService.getHelpArticleById(article.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          fullArticle => {
            if (fullArticle) {
              this.selectedArticle = fullArticle;
              if (fullArticle.multimedia) {
                this.loadArticleContent(fullArticle);
              } else {
                this.isLoadingArticle = false;
              }
            } else {
              this.isLoadingArticle = false;
            }
          },
          error => {
            console.error('Error loading article:', error);
            this.isLoadingArticle = false;
          }
        );
    }
  }

  /**
   * Load article content based on multimedia type (HTML, PDF, MARKDOWN)
   * Uses HttpClient via service to avoid CORS issues
   */
  private loadArticleContent(article: HelpArticle): void {
    if (!article.multimedia) {
      this.isLoadingArticle = false;
      return;
    }

    const { type, url } = article.multimedia;
    this.isLoadingArticle = true;

    switch (type) {
      case 'HTML':
        // Use iframe to display HTML content (avoids CORS issues with CloudFront)
        this.safeHtmlUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.isLoadingArticle = false;
        break;

      case 'MARKDOWN':
        // Fetch Markdown content using HttpClient (avoids CORS)
        this.chatbotService.fetchContentFromUrl(url)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            content => {
              this.articleMarkdownContent = content;
              this.isLoadingArticle = false;
              this.cdr.detectChanges();
            },
            error => {
              console.error('Error fetching Markdown content:', error);
              this.isLoadingArticle = false;
              this.cdr.detectChanges();
            }
          );
        break;

      case 'PDF':
        // Sanitize PDF URL for iframe embedding
        this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.isLoadingArticle = false;
        break;

      default:
        console.warn('Unknown multimedia type:', type);
        this.isLoadingArticle = false;
    }
  }

  // Private helper methods
  loadConversations(): void {
    this.isLoadingConversations = true;
    this.conversationsPage = 0; // Reset to page 0
    this.hasMoreConversations = true;

    this.chatbotService.getConversations(this.conversationsPage, this.conversationsSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          this.conversations = response.conversations;

          // Only allow more loading if response has data AND is not last page
          this.hasMoreConversations = !response.last && response.conversations.length > 0;
          this.isLoadingConversations = false;
        },
        error => {
          console.error('Error loading conversations:', error);
          this.conversations = [];
          this.hasMoreConversations = false;
          this.isLoadingConversations = false;
        }
      );
  }

  loadHelpCollections(): void {
    this.isLoadingHelpCollections = true;
    this.chatbotService.getHelpCollections()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        collections => {
          // Map Font Awesome icons to Bootstrap Icons
          this.helpCollections = collections.map(collection => ({
            ...collection,
            icon: this.mapIconToBootstrap(collection.icon),
            articles: collection.articles.map(article => ({
              ...article,
              icon: this.mapIconToBootstrap(article.icon)
            }))
          }));
          this.isLoadingHelpCollections = false;
        },
        error => {
          console.error('Error loading help collections:', error);
          this.isLoadingHelpCollections = false;
        }
      );
  }

  /**
   * Maps Font Awesome icon classes to Bootstrap Icons equivalents
   * Falls back to a default icon if no mapping exists
   */
  private mapIconToBootstrap(icon: string | undefined): string {
    if (!icon) return 'bi bi-folder';
    
    // If already a Bootstrap icon, return as-is
    if (icon.startsWith('bi-') || icon.startsWith('bi ')) {
      return icon.startsWith('bi ') ? icon : `bi ${icon}`;
    }

    // Font Awesome to Bootstrap Icons mapping
    const iconMap: { [key: string]: string } = {
      // Products & Shopping
      'fa-solid fa-box-open': 'bi bi-box-seam',
      'fa-solid fa-box': 'bi bi-box',
      'fa-solid fa-boxes': 'bi bi-boxes',
      'fa-solid fa-shopping-cart': 'bi bi-cart',
      'fa-solid fa-cart-shopping': 'bi bi-cart',
      'fa-solid fa-store': 'bi bi-shop',
      'fa-solid fa-shop': 'bi bi-shop',
      'fa-solid fa-basket-shopping': 'bi bi-basket',
      'fa-solid fa-tag': 'bi bi-tag',
      'fa-solid fa-tags': 'bi bi-tags',
      
      // Users & Account
      'fa-solid fa-user': 'bi bi-person',
      'fa-solid fa-users': 'bi bi-people',
      'fa-solid fa-user-circle': 'bi bi-person-circle',
      'fa-solid fa-user-cog': 'bi bi-person-gear',
      'fa-solid fa-user-shield': 'bi bi-person-badge',
      
      // Settings & Tools
      'fa-solid fa-cog': 'bi bi-gear',
      'fa-solid fa-cogs': 'bi bi-gear-wide-connected',
      'fa-solid fa-gear': 'bi bi-gear',
      'fa-solid fa-gears': 'bi bi-gear-wide-connected',
      'fa-solid fa-wrench': 'bi bi-wrench',
      'fa-solid fa-sliders': 'bi bi-sliders',
      'fa-solid fa-tools': 'bi bi-tools',
      
      // Money & Payments
      'fa-solid fa-money-bill': 'bi bi-cash',
      'fa-solid fa-credit-card': 'bi bi-credit-card',
      'fa-solid fa-wallet': 'bi bi-wallet',
      'fa-solid fa-dollar-sign': 'bi bi-currency-dollar',
      'fa-solid fa-euro-sign': 'bi bi-currency-euro',
      'fa-solid fa-coins': 'bi bi-coin',
      'fa-solid fa-receipt': 'bi bi-receipt',
      
      // Shipping & Delivery
      'fa-solid fa-truck': 'bi bi-truck',
      'fa-solid fa-shipping-fast': 'bi bi-truck',
      'fa-solid fa-truck-fast': 'bi bi-truck',
      'fa-solid fa-package': 'bi bi-box-seam',
      'fa-solid fa-location-dot': 'bi bi-geo-alt',
      'fa-solid fa-map-marker': 'bi bi-geo-alt',
      
      // Communication
      'fa-solid fa-envelope': 'bi bi-envelope',
      'fa-solid fa-message': 'bi bi-chat',
      'fa-solid fa-comments': 'bi bi-chat-dots',
      'fa-solid fa-phone': 'bi bi-telephone',
      'fa-solid fa-bell': 'bi bi-bell',
      
      // Files & Documents
      'fa-solid fa-file': 'bi bi-file',
      'fa-solid fa-file-text': 'bi bi-file-text',
      'fa-solid fa-folder': 'bi bi-folder',
      'fa-solid fa-folder-open': 'bi bi-folder2-open',
      'fa-solid fa-image': 'bi bi-image',
      'fa-solid fa-images': 'bi bi-images',
      
      // Charts & Analytics
      'fa-solid fa-chart-bar': 'bi bi-bar-chart',
      'fa-solid fa-chart-line': 'bi bi-graph-up',
      'fa-solid fa-chart-pie': 'bi bi-pie-chart',
      'fa-solid fa-analytics': 'bi bi-graph-up-arrow',
      
      // General
      'fa-solid fa-question-circle': 'bi bi-question-circle',
      'fa-solid fa-question': 'bi bi-question-lg',
      'fa-solid fa-info-circle': 'bi bi-info-circle',
      'fa-solid fa-info': 'bi bi-info-lg',
      'fa-solid fa-exclamation': 'bi bi-exclamation-lg',
      'fa-solid fa-exclamation-circle': 'bi bi-exclamation-circle',
      'fa-solid fa-check': 'bi bi-check-lg',
      'fa-solid fa-check-circle': 'bi bi-check-circle',
      'fa-solid fa-times': 'bi bi-x-lg',
      'fa-solid fa-times-circle': 'bi bi-x-circle',
      'fa-solid fa-star': 'bi bi-star',
      'fa-solid fa-heart': 'bi bi-heart',
      'fa-solid fa-home': 'bi bi-house',
      'fa-solid fa-house': 'bi bi-house',
      'fa-solid fa-book': 'bi bi-book',
      'fa-solid fa-lightbulb': 'bi bi-lightbulb',
      'fa-solid fa-rocket': 'bi bi-rocket',
      'fa-solid fa-paint-brush': 'bi bi-brush',
      'fa-solid fa-palette': 'bi bi-palette',
      'fa-solid fa-globe': 'bi bi-globe',
      'fa-solid fa-link': 'bi bi-link-45deg',
      'fa-solid fa-lock': 'bi bi-lock',
      'fa-solid fa-unlock': 'bi bi-unlock',
      'fa-solid fa-key': 'bi bi-key',
      'fa-solid fa-search': 'bi bi-search',
      'fa-solid fa-magnifying-glass': 'bi bi-search',
      'fa-solid fa-plus': 'bi bi-plus-lg',
      'fa-solid fa-minus': 'bi bi-dash-lg',
      'fa-solid fa-edit': 'bi bi-pencil',
      'fa-solid fa-pencil': 'bi bi-pencil',
      'fa-solid fa-trash': 'bi bi-trash',
      'fa-solid fa-download': 'bi bi-download',
      'fa-solid fa-upload': 'bi bi-upload',
      'fa-solid fa-share': 'bi bi-share',
      'fa-solid fa-calendar': 'bi bi-calendar',
      'fa-solid fa-clock': 'bi bi-clock',
      'fa-solid fa-list': 'bi bi-list',
      'fa-solid fa-grid': 'bi bi-grid',
      
      // Regular variants (fa-regular)
      'fa-regular fa-box-open': 'bi bi-box-seam',
      'fa-regular fa-folder': 'bi bi-folder',
      'fa-regular fa-file': 'bi bi-file',
    };

    // Try exact match first
    if (iconMap[icon]) {
      return iconMap[icon];
    }

    // Try to extract just the icon name and match
    const iconName = icon.split(' ').pop() || '';
    for (const [faIcon, bsIcon] of Object.entries(iconMap)) {
      if (faIcon.endsWith(iconName)) {
        return bsIcon;
      }
    }

    // Default fallback
    return 'bi bi-file-text';
  }

  addWelcomeMessage(): void {
    if (this.messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        id: this.generateMessageId(),
        text: this.translate.instant('chatbot_welcome_message'),
        isUser: false,
        timestamp: new Date(),
        type: 'text'
      };
      this.messages.push(welcomeMessage);
    }
  }

  generateMessageId(): string {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  scrollToBottom(): void {
    try {
      const messagesContainer = document.querySelector('.chatbot-messages');
      if (messagesContainer) {
        requestAnimationFrame(() => {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        });
      }
    } catch (error) {
      console.error('Error scrolling to bottom:', error);
    }
  }

  // Toggle message expansion to show metadata
  toggleMessageExpand(messageId: string): void {
    if (this.expandedMessageId === messageId) {
      this.expandedMessageId = null;
    } else {
      this.expandedMessageId = messageId;
    }
  }

  // Calculate relative time (e.g., "5s", "19m", "2h", "1d", "2mo", "1y")
  getRelativeTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    if (years > 0) {
      return `${years}y`;
    } else if (months > 0) {
      return `${months}mo`;
    } else if (days > 0) {
      return `${days}d`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else if (minutes > 0) {
      return `${minutes}m`;
    } else if (seconds > 0) {
      return `${seconds}s`;
    } else {
      return 'now';
    }
  }

  // Keyboard event handlers
  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // Dismiss usage limit alert
  dismissUsageLimitAlert(): void {
    this.isUsageLimitExceeded = false;
    this.usageLimitDetail = null;
  }

  // Track by functions for *ngFor optimization
  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id;
  }

  trackByConversationId(index: number, conversation: ChatConversation): string {
    return conversation.id;
  }

  trackByArticleId(index: number, article: HelpArticle): string {
    return article.id;
  }

  trackByCollectionId(index: number, collection: HelpCollection): string {
    return collection.id;
  }

  // Track by function for navigation routes
  trackByRouteUri(index: number, route: NavigationRoute): string {
    return route.uri;
  }

  // Map of placeholder names to localStorage keys
  private placeholderToLocalStorageKey: { [key: string]: string } = {
    'siteId': 'selectedShopId',
    'siteName': 'selectedShopName'
  };

  /**
   * Check if all placeholders in a route URI can be resolved from localStorage.
   * Returns true only if ALL placeholders have corresponding values in localStorage.
   */
  canResolveRoute(route: NavigationRoute): boolean {
    if (!route?.uri) {
      return false;
    }

    // Extract all :placeholder patterns from the URI
    const placeholderPattern = /:([a-zA-Z_][a-zA-Z0-9_]*)/g;
    const matches = route.uri.matchAll(placeholderPattern);
    const placeholders = Array.from(matches, m => m[1]);

    // If no placeholders, route is always resolvable
    if (placeholders.length === 0) {
      return true;
    }

    // Check if ALL placeholders can be resolved
    for (const placeholder of placeholders) {
      const localStorageKey = this.placeholderToLocalStorageKey[placeholder];

      // If we don't have a mapping for this placeholder, we can't resolve it
      if (!localStorageKey) {
        return false;
      }

      // If the localStorage value is missing or empty, we can't resolve it
      const value = localStorage.getItem(localStorageKey);
      if (!value || value.trim() === '') {
        return false;
      }
    }

    return true;
  }

  // Navigate to route from extraData
  navigateToRoute(route: NavigationRoute): void {
    // Replace dynamic parameters (:siteId, :siteName) with actual values
    let uri = route.uri;

    const siteId = localStorage.getItem('selectedShopId');
    const siteName = localStorage.getItem('selectedShopName');

    if (siteId) {
      uri = uri.replace(':siteId', siteId);
    }
    if (siteName) {
      uri = uri.replace(':siteName', siteName);
    }

    // Close chatbot before navigating
    this.closeChatbot();

    // Navigate to the route
    this.router.navigate([uri]);
  }

  // Get popular articles from all collections
  getPopularArticles(): HelpArticle[] {
    const allArticles: HelpArticle[] = [];
    this.helpCollections.forEach(collection => {
      allArticles.push(...collection.articles);
    });
    // Return first 5 articles (or all if less than 5)
    return allArticles.slice(0, 5);
  }

  // ============================================
  // Infinite Scroll Methods
  // ============================================

  // Load more conversations (scroll infinito)
  loadMoreConversations(): void {
    if (this.isLoadingConversations || !this.hasMoreConversations) {
      return;
    }

    this.isLoadingConversations = true;
    this.conversationsPage++;

    this.chatbotService.getConversations(this.conversationsPage, this.conversationsSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          // Only append if there are new conversations
          if (response.conversations && response.conversations.length > 0) {
            this.conversations = [...this.conversations, ...response.conversations];
          }

          // Stop loading more if no data or last page
          this.hasMoreConversations = !response.last && response.conversations.length > 0;
          this.isLoadingConversations = false;
        },
        error => {
          console.error('Error loading more conversations:', error);
          this.hasMoreConversations = false; // Stop trying on error
          this.isLoadingConversations = false;
          this.conversationsPage--; // Revert page increment on error
        }
      );
  }

  // Load older messages (scroll infinito hacia arriba)
  loadOlderMessages(): void {
    if (this.isLoadingMessages || !this.hasMoreMessages || !this.currentConversation) {
      return;
    }

    this.isLoadingMessages = true;
    this.messagesPage++;

    const scrollContainer = document.querySelector('.chatbot-messages');
    const previousScrollHeight = scrollContainer ? scrollContainer.scrollHeight : 0;

    this.chatbotService.getConversationMessages(
      this.currentConversation.id,
      this.messagesPage,
      this.messagesSize
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        response => {
          // Only prepend if there are new messages
          if (response.messages && response.messages.length > 0) {
            this.messages = [...response.messages, ...this.messages];

            if (this.currentConversation) {
              this.currentConversation.messages = this.messages;
            }

            // Maintain scroll position after prepending messages
            this.cdr.detectChanges();
            if (scrollContainer) {
              const newScrollHeight = scrollContainer.scrollHeight;
              scrollContainer.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }

          // Stop loading more if no data or last page
          this.hasMoreMessages = !response.last && response.messages.length > 0;
          this.isLoadingMessages = false;
        },
        error => {
          console.error('Error loading older messages:', error);
          this.hasMoreMessages = false; // Stop trying on error
          this.isLoadingMessages = false;
          this.messagesPage--; // Revert page increment on error
        }
      );
  }

  // Detect scroll for conversations list
  onConversationsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 50; // pixels from bottom to trigger load

    if (element.scrollHeight - element.scrollTop - element.clientHeight < threshold) {
      this.loadMoreConversations();
    }
  }

  // Detect scroll for messages (top scroll)
  onMessagesScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const threshold = 50; // pixels from top to trigger load

    if (element.scrollTop < threshold) {
      this.loadOlderMessages();
    }
  }
}
